/**
 * TimelineStepperControls — a generic, presentational stepped-timeline scrubber.
 *
 * Domain-agnostic on purpose (mirrors the crypto Workbench PlaybackControls /
 * TimelineBar pattern, but coupled to NOTHING — no stores, no Cytoscape, no
 * transaction schema). Steps are integers; the caller supplies optional labels
 * and accent colours. Reusable by the fusion demo (steps = ingestion stages) and
 * by the Workbench transaction playback (steps = transaction index).
 */
import { Play, Pause, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';

export interface TimelineStepperControlsProps {
  currentStep: number;            // 1-based
  totalSteps: number;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  onRestart: () => void;
  onStepChange: (next: number) => void;
  stepLabels?: string[];          // length = totalSteps (optional)
  stepSubs?: string[];            // secondary line per step (optional)
  accentColors?: string[];        // length = totalSteps (optional)
  /** Visible label beside the primary play control (Fusion: "Play stages"). */
  playLabel?: string;
  className?: string;
}

export function TimelineStepperControls({
  currentStep, totalSteps, isPlaying, onPlay, onPause, onRestart, onStepChange,
  stepLabels, stepSubs, accentColors, playLabel, className,
}: TimelineStepperControlsProps) {
  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);
  const atEnd = currentStep >= totalSteps;
  const accent = (n: number) => accentColors?.[n - 1] || '#2f6fed';
  const curLabel = stepLabels?.[currentStep - 1];
  const curSub = stepSubs?.[currentStep - 1];
  const primaryTitle = isPlaying ? 'Pause' : atEnd ? 'Replay from stage 1' : 'Play stages';
  const primaryText = playLabel ?? (isPlaying ? 'Pause' : atEnd ? 'Replay' : 'Play');

  return (
    <div className={`flex items-center gap-3 ${className || ''}`}>
      <div className="flex items-center gap-1">
        {/* Restart only when it's a distinct action — at stage 1 it duplicates Play, at the end it duplicates the Replay button (QA F-6). */}
        {currentStep > 1 && currentStep < totalSteps && (
          <button type="button" onClick={onRestart} title="Restart from stage 1" aria-label="Restart from stage 1" className="p-1.5 rounded-md border border-border bg-secondary hover:bg-secondary/70 text-foreground">
            <RotateCcw size={14} />
          </button>
        )}
        <button type="button" onClick={() => onStepChange(currentStep - 1)} disabled={currentStep <= 1} title="Previous stage" aria-label="Previous stage"
          className="p-1.5 rounded-md border border-border bg-secondary hover:bg-secondary/70 disabled:opacity-30">
          <ChevronLeft size={14} />
        </button>
        <button
          type="button"
          onClick={atEnd ? onRestart : (isPlaying ? onPause : onPlay)}
          title={primaryTitle}
          aria-label={primaryTitle}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white border border-sky-700 text-[11px] font-semibold"
        >
          {isPlaying ? <Pause size={14} /> : atEnd ? <RotateCcw size={14} /> : <Play size={14} />}
          <span>{primaryText}</span>
        </button>
        <button type="button" onClick={() => onStepChange(currentStep + 1)} disabled={atEnd} title="Next stage" aria-label="Next stage"
          className="p-1.5 rounded-md border border-border bg-secondary hover:bg-secondary/70 disabled:opacity-30">
          <ChevronRight size={14} />
        </button>
      </div>

      {/* segmented track */}
      <div className="flex-1 flex items-center min-w-0">
        {steps.map((n, i) => {
          const done = n <= currentStep;
          const isCur = n === currentStep;
          return (
            <div key={n} className="flex items-center" style={{ flex: i === steps.length - 1 ? '0 0 auto' : '1 1 0%' }}>
              <button
                onClick={() => onStepChange(n)}
                title={stepLabels?.[n - 1] || `Stage ${n}`}
                className="relative grid place-items-center rounded-full"
                style={{
                  width: isCur ? 16 : 11, height: isCur ? 16 : 11,
                  background: done ? accent(n) : 'transparent',
                  border: `2px solid ${done ? accent(n) : '#cbd5e1'}`,
                  boxShadow: isCur ? `0 0 0 4px ${accent(n)}33` : 'none',
                }}
              >
                {isCur && isPlaying && <span className="absolute inset-0 rounded-full animate-ping" style={{ background: accent(n), opacity: 0.4 }} />}
              </button>
              {i < steps.length - 1 && (
                <div className="h-[3px] flex-1 mx-0.5 rounded-full" style={{ background: n < currentStep ? accent(n) : '#e2e8f0' }} />
              )}
            </div>
          );
        })}
      </div>

      <div className="shrink-0 text-right min-w-[8.5rem]">
        <div className="text-[11px] font-semibold leading-tight truncate">{curLabel || `Stage ${currentStep} / ${totalSteps}`}</div>
        <div className="text-[9px] text-muted-foreground leading-tight truncate">{curSub || `Stage ${currentStep} of ${totalSteps}`}</div>
      </div>
    </div>
  );
}
